import {
  BaseEdge,
  EdgeLabelRenderer,
  getEdgeCenter,
  useInternalNode,
  useStoreApi,
  type Edge,
  type EdgeProps,
} from '@xyflow/react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { GraphColors } from '../../../bpmn-graph.constants'
import { SequenceEdgeData } from '../bpmn-graph.mapper'
import { useBpmnDiagramContext } from '../bpmn-diagram.context'
import { useIsEdgeDraggedOver } from '../edge-drag.context'
import SequenceArrowMarker from './SequenceArrowMarker'
import {
  EdgePoint,
  EdgeRect,
  buildWaypointsPath,
  interiorIndexes,
  isHorizontal,
  moveSegmentAndMergeForDrag,
  moveVertexOrthogonal,
  previewRoute,
  normalizeOrthogonalPoints,
  removeWaypointOrthogonal,
  repairOrthogonalPoints,
  snapEndpointsToNodes,
} from './orthogonal-edge.util'

/** Source-end dot radius */
const SOURCE_DOT_RADIUS = 4
/** Sequence lines render at 0.6 opacity */
const LINE_OPACITY = 0.6
/** Radius of a draggable waypoint control point (flow px) */
const VERTEX_RADIUS = 4
/** Larger transparent circle around a control point for an easier grab target */
const VERTEX_HIT_RADIUS = 9
/** Radius of the "add point" ghost handle at a segment midpoint */
const ADD_HANDLE_RADIUS = 4
/** A pointer move beyond this (flow px) counts as a drag, not a click */
const DRAG_THRESHOLD = 2
/** Width of the invisible hover zone that reveals the control points (matches RF's default interactionWidth) */
const HOVER_ZONE_WIDTH = 20

function rectFromInternalNode(
  node:
    | { internals: { positionAbsolute: EdgePoint }; measured: { width?: number; height?: number } }
    | undefined
): EdgeRect | null {
  const width = node?.measured?.width
  const height = node?.measured?.height

  return node && width && height
    ? { x: node.internals.positionAbsolute.x, y: node.internals.positionAbsolute.y, width, height }
    : null
}

function samePoints(a: EdgePoint[], b: EdgePoint[]): boolean {
  return (
    a.length === b.length &&
    a.every((point, index) => point.x === b[index].x && point.y === b[index].y)
  )
}

interface DragBase {
  /** Persisted waypoints from pointer-down; never changes when the gesture rebases. */
  initial: EdgePoint[]
  /** Current movement base (moves are absolute until a topology change rebases it). */
  origin: EdgePoint[]
  startClientX: number
  startClientY: number
  live: EdgePoint[]
  moved: boolean
}

/** Dragging a solid corner control point (repositions the bend in 2D). */
interface VertexDrag extends DragBase {
  type: 'vertex'
  index: number
}

/** Dragging a ghost midpoint handle (moves the whole segment perpendicular). */
interface SegmentDrag extends DragBase {
  type: 'segment'
  segmentIndex: number
  horizontal: boolean
}

type EdgeDrag = VertexDrag | SegmentDrag

function SequenceEdge({
  id,
  source: sourceNodeId,
  target: targetNodeId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
  selected,
}: EdgeProps<Edge<SequenceEdgeData>>) {
  const canonicalPoints = data.points
  // Keep the final local frame mounted until the synchronous Effector update has
  // crossed the Angular→React bridge. This prevents mouse-up from briefly
  // reverting to the previous persisted points.
  const [pendingPoints, setPendingPoints] = useState<EdgePoint[] | null>(null)
  useEffect(() => {
    if (!pendingPoints) {
      return undefined
    }
    if (samePoints(canonicalPoints, pendingPoints)) {
      setPendingPoints(null)

      return undefined
    }
    const timeout = window.setTimeout(() => setPendingPoints(null), 5000)

    return () => window.clearTimeout(timeout)
  }, [canonicalPoints, pendingPoints])
  useEffect(() => {
    if (data.readonly) {
      setPendingPoints(null)
    }
  }, [data.readonly])
  const waypoints = pendingPoints ?? canonicalPoints

  const sourceInternal = useInternalNode(sourceNodeId)
  const targetInternal = useInternalNode(targetNodeId)
  const liveSourceRect = rectFromInternalNode(sourceInternal)
  const liveTargetRect = rectFromInternalNode(targetInternal)
  const draggedOver = useIsEdgeDraggedOver(id)
  const { onSequenceSegmentMoved } = useBpmnDiagramContext()
  const flowStoreApi = useStoreApi()
  const [edgeDrag, setEdgeDrag] = useState<EdgeDrag | null>(null)
  const [hovered, setHovered] = useState(false)
  // Authoritative drag record read by the window listeners; the state above only
  // mirrors it so the live polyline re-renders.
  const dragRef = useRef<EdgeDrag | null>(null)

  // The active interaction and the committed model render through the same list.
  const displayPoints = edgeDrag?.live ?? waypoints
  const [labelX, labelY] = useMemo(
    () => getEdgeCenter({ sourceX, sourceY, targetX, targetY }),
    [sourceX, sourceY, targetX, targetY]
  )

  // Editing is available on hover for any non-readonly, non-preview edge.
  const canEdit = !data?.readonly && !data?.preview
  const editable = canEdit && (hovered || !!edgeDrag)
  const interior = useMemo(() => interiorIndexes(displayPoints), [displayPoints])

  // Persist the exact canonical point list already displayed on the final frame.
  const commitPoints = (points: EdgePoint[]): void => {
    if (data?.sequence) {
      setPendingPoints(points)
      onSequenceSegmentMoved({ sequence: data.sequence, points })
    }
  }

  // commitPoints closes over the current sequence; expose the latest to the
  // window listeners without re-subscribing them on every live re-render.
  const commitRef = useRef(commitPoints)
  commitRef.current = commitPoints
  const rectsRef = useRef({
    source: liveSourceRect ?? data.sourceRect,
    target: liveTargetRect ?? data.targetRect,
  })
  rectsRef.current = {
    source: liveSourceRect ?? data.sourceRect,
    target: liveTargetRect ?? data.targetRect,
  }

  // Start a drag. The move/up gestures are handled on `window` (below) rather than
  // on the SVG handle, so the drag survives the handle unmounting — which happens
  // the instant a segment drag inserts riser corners and the control points
  // re-render.
  const beginDrag = (event: React.PointerEvent<SVGCircleElement>, record: EdgeDrag): void => {
    event.stopPropagation()
    dragRef.current = record
    setEdgeDrag(record)
  }

  const dragStart = (
    event: React.PointerEvent<SVGCircleElement>
  ): Pick<DragBase, 'initial' | 'origin' | 'startClientX' | 'startClientY' | 'live' | 'moved'> => ({
    initial: waypoints,
    origin: waypoints,
    startClientX: event.clientX,
    startClientY: event.clientY,
    live: waypoints,
    moved: false,
  })

  // Drag a solid corner control point (repositions the bend in 2D).
  const beginVertexDrag = (event: React.PointerEvent<SVGCircleElement>, index: number): void =>
    beginDrag(event, { type: 'vertex', index, ...dragStart(event) })

  // Drag a ghost midpoint handle: moves the whole segment perpendicular to itself,
  // inserting right-angle risers to any anchored endpoint (React Flow editable-edge
  // "step" behaviour). This is what lets a straight edge gain a bend — so the ghost
  // handle does something on every segment, not only where a solid vertex exists.
  const beginSegmentDrag = (
    event: React.PointerEvent<SVGCircleElement>,
    segmentIndex: number
  ): void =>
    beginDrag(event, {
      type: 'segment',
      segmentIndex,
      horizontal: isHorizontal(waypoints[segmentIndex], waypoints[segmentIndex + 1]),
      ...dragStart(event),
    })

  const deleteVertex = (event: React.MouseEvent, index: number): void => {
    event.stopPropagation()
    const next = removeWaypointOrthogonal(waypoints, index)
    // Structural corners return null — they can be moved but not deleted.
    if (next) {
      commitPoints(next)
    }
  }

  const dragging = !!edgeDrag
  useEffect(() => {
    if (!dragging) {
      return undefined
    }
    const onMove = (event: PointerEvent): void => {
      const record = dragRef.current
      if (!record) {
        return
      }
      const zoom = flowStoreApi.getState().transform[2]
      const deltaX = (event.clientX - record.startClientX) / zoom
      const deltaY = (event.clientY - record.startClientY) / zoom
      const { source, target } = rectsRef.current
      const hasMoved =
        record.moved || Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD

      if (record.type === 'vertex') {
        const moved = moveVertexOrthogonal(
          record.origin,
          record.index,
          record.origin[record.index].x + deltaX,
          record.origin[record.index].y + deltaY
        )
        // Preview only — see previewRoute: the drop canonicalizes for real.
        const live = previewRoute(
          normalizeOrthogonalPoints(repairOrthogonalPoints(moved)),
          source,
          target
        )
        dragRef.current = { ...record, live, moved: hasMoved }
      } else {
        const update = moveSegmentAndMergeForDrag(
          record.origin,
          record.segmentIndex,
          record.horizontal ? deltaY : deltaX,
          source,
          target
        )
        // Once a connector disappears, continue from the merged point list and
        // the current pointer position. The active segment therefore survives
        // its index/topology change and remains attached to this same gesture.
        dragRef.current = update.merged
          ? {
              ...record,
              origin: update.points,
              segmentIndex: update.segmentIndex,
              startClientX: event.clientX,
              startClientY: event.clientY,
              live: update.points,
              moved: hasMoved,
            }
          : { ...record, live: update.points, moved: hasMoved }
      }
      setEdgeDrag(dragRef.current)
    }
    const onUp = (): void => {
      const record = dragRef.current
      if (record?.moved) {
        const { source, target } = rectsRef.current
        const dropped = normalizeOrthogonalPoints(
          repairOrthogonalPoints(snapEndpointsToNodes(record.live, source, target))
        )
        // A segment released on its original node side snaps back to center and
        // is usually a no-op. Crossing beyond the node creates and persists the
        // new perpendicular endpoint leg visible on the final drag frame.
        if (!samePoints(dropped, record.initial)) {
          commitRef.current(dropped)
        }
      }
      dragRef.current = null
      setEdgeDrag(null)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)

    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [dragging, flowStoreApi])

  // One polyline for both rendering and control points — sharp step corners
  // (matches the RF editable-edge "Step" example) and guarantees that every
  // handle sits exactly on the visible edge.
  const path = buildWaypointsPath(displayPoints)
  // Colour ladder: readonly → hovered/dragged-over → selected → default;
  // the AI draft preview renders every sequence grey.
  // Audit-state palette (process-instance viewer) sits between preview and
  // readonly — preview is mutually exclusive with audit in practice, but this
  // keeps preview's grey wash intact if both were ever set.
  const color = data?.preview
    ? 'var(--flx-bpmn-edge-color, rgba(71, 82, 99, 0.6))'
    : data?.auditStyles
      ? data.auditStyles.defaultColor
      : data?.readonly
        ? 'var(--flx-bpmn-edge-color, rgba(71, 82, 99, 0.4))'
        : draggedOver
          ? GraphColors.blue_300
          : selected || hovered
            ? GraphColors.blue_500
            : 'var(--flx-bpmn-edge-color, rgba(71, 82, 99, 0.6))'
  const markerId = `bpmn-sequence-arrow-${id}`
  const conditionKey = data?.sequence?.conditionKey
  const sourcePoint = displayPoints[0] ?? { x: sourceX, y: sourceY }

  return (
    <>
      <SequenceArrowMarker
        id={markerId}
        color={color}
      />
      <BaseEdge
        id={id}
        path={path}
        markerEnd={`url(#${markerId})`}
        style={{ stroke: color, strokeWidth: 2, opacity: LINE_OPACITY }}
        data-testid={`bpmn-sequence-${data?.sequence?.id}`}
      />
      {/* Source-end dot: edges render under nodes, so the half inside the source
          node is hidden and it reads as a semicircle */}
      <circle
        cx={sourcePoint.x}
        cy={sourcePoint.y}
        r={SOURCE_DOT_RADIUS}
        fill={color}
      />
      {canEdit && (
        <g
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Wide transparent hover zone: reveals the control points on hover
              without selecting the edge (selecting also opens the actions menu).
              The handles live inside this same <g>, so moving from the line onto a
              handle keeps the hover state (mouseleave ignores descendants). */}
          <path
            d={path}
            fill="none"
            stroke="transparent"
            strokeWidth={HOVER_ZONE_WIDTH}
            style={{ pointerEvents: 'stroke' }}
          />
          {editable && (
            <>
              {/* "Add point" ghost handles at each segment midpoint (editable-edge
                  style): dragging one inserts a waypoint and shapes it in the same
                  gesture. Hidden mid-drag so the inserted vertex reads as a single
                  solid control point rather than doubling with its ghost. */}
              {!edgeDrag &&
                displayPoints.slice(0, -1).map((from, index) => {
                  const to = displayPoints[index + 1]
                  const midX = (from.x + to.x) / 2
                  const midY = (from.y + to.y) / 2

                  return (
                    <circle
                      key={`add-${index}`}
                      // nodrag/nopan: without them React Flow starts a viewport pan on
                      // pointerdown and the handle never receives the gesture
                      className="nodrag nopan"
                      data-testid={`bpmn-sequence-add-${data.sequence.id}-${index}`}
                      cx={midX}
                      cy={midY}
                      r={ADD_HANDLE_RADIUS}
                      fill={GraphColors.neutral_50}
                      stroke={GraphColors.blue_500}
                      strokeWidth={1.5}
                      // Cursor hints the perpendicular drag axis of this segment.
                      style={{
                        cursor: isHorizontal(from, to) ? 'ns-resize' : 'ew-resize',
                        pointerEvents: 'all',
                      }}
                      onPointerDown={(event) => beginSegmentDrag(event, index)}
                    />
                  )
                })}
              {/* Draggable waypoint control points (orthogonal-constrained). Double-click
              removes a bend when the path stays orthogonal. */}
              {interior.map((index) => {
                const point = displayPoints[index]

                return (
                  <g key={`vertex-${index}`}>
                    <circle
                      // nodrag/nopan: without them React Flow starts a viewport pan on
                      // pointerdown and the handle never receives the drag gesture
                      className="nodrag nopan"
                      cx={point.x}
                      cy={point.y}
                      r={VERTEX_HIT_RADIUS}
                      fill="transparent"
                      data-testid={`bpmn-sequence-vertex-${data.sequence.id}-${index}`}
                      style={{ cursor: 'move', pointerEvents: 'all' }}
                      onPointerDown={(event) => beginVertexDrag(event, index)}
                      onDoubleClick={(event) => deleteVertex(event, index)}
                    />
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={VERTEX_RADIUS}
                      fill={GraphColors.blue_500}
                      stroke={GraphColors.neutral_50}
                      strokeWidth={1.5}
                      style={{ pointerEvents: 'none' }}
                    />
                  </g>
                )
              })}
            </>
          )}
        </g>
      )}
      {conditionKey && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 10,
              color: GraphColors.neutral_500,
              background: GraphColors.neutral_50,
              padding: '1px 4px',
              borderRadius: 4,
              pointerEvents: 'none',
            }}
          >
            {conditionKey}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

export default memo(SequenceEdge)
